import { useState } from "react";
import { Printer, X } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

const fmt = (n) =>
  new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtDateOnly = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const BUSINESS = {
  name: "CRIYE CELULARES",
  ig1: "@criyecelulares",
  ig2: "@criyecelulares2",
  ig3: "@mecanicoargentina",
  phone: "11 3158-2274",
  whatsapp: "https://wa.me/5491131582274",
};

const FORMATS = {
  "58mm": { width: "58mm", pad: "3mm", fontBase: "12px", fontTitle: "15px", lineHeight: 1.4, weight: 700, qr: 70 },
  "80mm": { width: "80mm", pad: "4mm", fontBase: "13px", fontTitle: "18px", lineHeight: 1.4, weight: 600, qr: 100 },
  a4: { width: "180mm", pad: "12mm", fontBase: "13px", fontTitle: "22px", lineHeight: 1.5, weight: 400, qr: 110 },
};

function Row({ label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "6px" }}>
      <span style={{ color: "#000", fontWeight: bold ? 800 : 600 }}>{label}</span>
      <span style={{ color: "#000", fontWeight: bold ? 800 : 500, textAlign: "right", overflowWrap: "anywhere" }}>{value}</span>
    </div>
  );
}

function Line() {
  return <div style={{ borderTop: "2px solid #000", margin: "5px 0", height: 0 }} />;
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontWeight: 900, textAlign: "center", margin: "6px 0 4px", letterSpacing: "1px", color: "#000", textTransform: "uppercase", fontSize: "0.9em", background: "#000", color: "#fff", padding: "2px 0" }}>
      {children}
    </div>
  );
}

function ClientCopy({ repair, cfg }) {
  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: "6px" }}>
        <div style={{ fontWeight: 900, fontSize: cfg.fontTitle, color: "#000" }}>{BUSINESS.name}</div>
        <div style={{ fontWeight: 700, fontSize: "0.9em", marginTop: "2px" }}>ORDEN DE REPARACIÓN</div>
        <div style={{ fontSize: "0.8em", fontWeight: 600 }}>Copia para el cliente</div>
      </div>
      <Line />
      <Row label="N° Orden" value={`#${repair.order_number}`} bold />
      <Row label="Fecha" value={fmtDate(repair.created_at)} />
      <Row label="Cliente" value={repair.customer_name} />
      {repair.customer_phone && <Row label="Tel" value={repair.customer_phone} />}
      <Line />
      <SectionTitle>Equipo</SectionTitle>
      <Row label="Marca" value={repair.device_brand || "—"} />
      <Row label="Modelo" value={repair.device_model || "—"} />
      {repair.device_color && <Row label="Color" value={repair.device_color} />}
      <Line />
      <SectionTitle>Reparación a realizar</SectionTitle>
      <div style={{ fontSize: "0.9em", color: "#000", fontWeight: 600, marginBottom: "4px" }}>{repair.repair_description}</div>
      <Line />
      <div style={{ fontSize: "1em" }}>
        <Row label="Presupuesto" value={`$${fmt(repair.budget)}`} bold />
        <Row label="Seña" value={`$${fmt(repair.deposit)}`} />
        <Row label="Saldo a abonar" value={`$${fmt(repair.balance)}`} bold />
        {repair.estimated_date && <Row label="Entrega estimada" value={fmtDateOnly(repair.estimated_date)} />}
      </div>
      <Line />
      <div style={{ fontSize: "0.8em", fontWeight: 600, textAlign: "center", lineHeight: 1.5 }}>
        <div>Presentar este ticket al retirar el equipo</div>
        <div>Garantía de 30 días desde la entrega</div>
        <div>Sin garantía por humedad, golpes o terceros</div>
      </div>
      <div style={{ display: "flex", justifyContent: "center", margin: "6px 0 2px" }}>
        <QRCodeSVG value={BUSINESS.whatsapp} size={cfg.qr} level="M" bgColor="#FFFFFF" fgColor="#000000" includeMargin={false} />
      </div>
      <div style={{ textAlign: "center", fontSize: "0.8em", fontWeight: 700 }}>
        <div>WhatsApp: {BUSINESS.phone}</div>
        <div>{BUSINESS.ig1}</div>
      </div>
    </div>
  );
}

function TechnicianCopy({ repair, cfg }) {
  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: "6px" }}>
        <div style={{ fontWeight: 900, fontSize: cfg.fontTitle, color: "#000" }}>{BUSINESS.name}</div>
        <div style={{ fontWeight: 700, fontSize: "0.9em", marginTop: "2px" }}>ORDEN DE REPARACIÓN</div>
        <div style={{ fontSize: "0.8em", fontWeight: 600 }}>Copia para el técnico</div>
      </div>
      <Line />
      <Row label="N° Orden" value={`#${repair.order_number}`} bold />
      <Row label="Fecha" value={fmtDate(repair.created_at)} />
      <Row label="Cliente" value={repair.customer_name} />
      {repair.customer_phone && <Row label="Tel" value={repair.customer_phone} />}
      <Row label="Recibió" value={repair.received_by} />
      <Line />
      <SectionTitle>Equipo</SectionTitle>
      <Row label="Marca" value={repair.device_brand || "—"} />
      <Row label="Modelo" value={repair.device_model || "—"} />
      {repair.device_color && <Row label="Color" value={repair.device_color} />}
      <Row label="IMEI" value={repair.device_imei || "—"} bold />
      <Row label="Clave" value={repair.device_password || "SIN CLAVE"} bold />
      {repair.device_accessories && <Row label="Accesorios" value={repair.device_accessories} />}
      {repair.device_condition && <Row label="Estado estético" value={repair.device_condition} />}
      <Line />
      <SectionTitle>Reparación a realizar</SectionTitle>
      <div style={{ fontSize: "0.9em", color: "#000", fontWeight: 600, marginBottom: "4px" }}>{repair.repair_description}</div>
      {repair.private_notes && (
        <>
          <Line />
          <SectionTitle>Notas internas</SectionTitle>
          <div style={{ fontSize: "0.9em", color: "#000", fontWeight: 600, marginBottom: "4px" }}>{repair.private_notes}</div>
        </>
      )}
      <Line />
      <div style={{ fontSize: "1em" }}>
        <Row label="Presupuesto" value={`$${fmt(repair.budget)}`} />
        <Row label="Seña" value={`$${fmt(repair.deposit)}`} />
        <Row label="Saldo" value={`$${fmt(repair.balance)}`} bold />
        {repair.estimated_date && <Row label="Entrega est." value={fmtDateOnly(repair.estimated_date)} />}
      </div>
      <Line />
      <div style={{ textAlign: "center", fontSize: "0.8em", fontWeight: 700 }}>
        <div>Técnico: {repair.technician_name || "—"}</div>
        <div>Tel: {BUSINESS.phone}</div>
      </div>
    </div>
  );
}

/**
 * RepairTicketPrint — Modal para imprimir las 2 copias de una orden de reparación.
 * Imprime ambas copias juntas (cliente + técnico) separadas por corte visual.
 *
 * Props:
 *   repair: objecto completo de la reparación
 *   onClose: () => void
 */
export default function RepairTicketPrint({ repair, onClose }) {
  const [format, setFormat] = useState("80mm");
  const cfg = FORMATS[format];

  if (!repair) return null;

  const bodyStyle = {
    fontFamily: "'Courier New', 'Lucida Console', monospace",
    fontSize: cfg.fontBase,
    fontWeight: cfg.weight,
    color: "#000",
    background: "#fff",
    padding: cfg.pad,
    width: format === "a4" ? "100%" : cfg.width,
    margin: "0 auto",
    lineHeight: cfg.lineHeight,
    WebkitFontSmoothing: "none",
    fontSmooth: "never",
    textRendering: "geometricPrecision",
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 print:hidden"
        data-testid="repair-print-modal"
      >
        <div className="glass-card rounded-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <Printer size={20} weight="duotone" className="text-turquoise" />
              Imprimir orden #{repair.order_number}
            </h3>
            <button onClick={onClose} data-testid="repair-print-close" aria-label="Cerrar">
              <X size={20} />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">Formato</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "58mm", label: "Térmica 58mm" },
                { key: "80mm", label: "Térmica 80mm" },
                { key: "a4", label: "Hoja A4" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setFormat(opt.key)}
                  data-testid={`repair-format-${opt.key}`}
                  className={`py-2 px-2 text-[11px] uppercase tracking-wider font-mono rounded-md border transition ${
                    format === opt.key
                      ? "border-turquoise bg-turquoise/10 text-turquoise"
                      : "border-white/10 hover:border-white/20 text-zinc-400"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <p className="text-[10px] text-zinc-500 font-mono mb-3">
            Se imprimen las 2 copias juntas: primero CLIENTE, después TÉCNICO.
          </p>

          <div className="bg-white text-black rounded-md p-3 mb-4 max-h-[380px] overflow-y-auto">
            <div style={bodyStyle}>
              <ClientCopy repair={repair} cfg={cfg} />
              <div style={{ borderTop: "3px solid #000", margin: "10px 0" }} />
              <TechnicianCopy repair={repair} cfg={cfg} />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              data-testid="repair-print-cancel"
              className="flex-1 py-2.5 rounded-md border border-white/10 text-sm font-medium hover:bg-white/5 transition"
            >
              Cerrar
            </button>
            <button
              onClick={() => window.print()}
              data-testid="repair-print-btn"
              className="flex-1 py-2.5 rounded-md text-sm font-semibold text-black bg-brand-gradient hover:opacity-90 transition flex items-center justify-center gap-2"
            >
              <Printer size={16} weight="bold" /> Imprimir 2 copias
            </button>
          </div>
        </div>
      </div>

      {/* Hidden printable area */}
      <div className="repair-print-only" aria-hidden>
        <div style={{ ...bodyStyle, width: cfg.width }}>
          <ClientCopy repair={repair} cfg={cfg} />
          <div style={{ borderTop: "3px solid #000", margin: "14px 0", position: "relative" }}>
            <span style={{ position: "absolute", right: 0, top: "-8px", fontSize: "0.7em", background: "#fff", paddingLeft: "6px" }}>✂ corte aquí</span>
          </div>
          <TechnicianCopy repair={repair} cfg={cfg} />
        </div>
      </div>

      <style>{`
        .repair-print-only { display: none; }
        @media print {
          html, body { background: #fff !important; }
          body * { visibility: hidden !important; }
          .repair-print-only, .repair-print-only * { visibility: visible !important; }
          .repair-print-only {
            display: block !important;
            position: absolute;
            left: 0; top: 0;
            width: 100%;
            color: #000 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .repair-print-only * {
            color: #000 !important;
            -webkit-font-smoothing: none !important;
            font-smooth: never !important;
            text-rendering: geometricPrecision !important;
          }
          @page {
            margin: 0;
            size: ${format === "a4" ? "A4" : `${cfg.width} auto`};
          }
        }
      `}</style>
    </>
  );
}
